import { RefreshCcwDotIcon, SendIcon } from "lucide-react";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import {
  CommandMenu,
  CommandMenuProvider,
  useCommandMenu,
} from "@/components/CommandMenu";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "@/components/ui/toast";
import type { TAccession } from "@/lib/types";
import { mutationResponse } from "@/utils/trpc";
import { GenerateAllDialog } from "../components/GenerateAllDialog";
import { ReportsList } from "../components/ReportsList";
import { usePendingReports } from "../hooks";

export function ReportsPage() {
  useTitle("Reports");

  const { accessions, queuedIds, fetching, markSubmitted } =
    usePendingReports();
  const allQueued =
    accessions.length > 0 && accessions.every((a) => queuedIds.includes(a.id));

  return (
    <>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb>Pending Reports</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        <SubHeader.Right>
          <GenerateAllDialog
            accessionIds={accessions.map((a) => a.id)}
            onQueued={markSubmitted}
          >
            <Button isDisabled={accessions.length === 0 || allQueued} size="sm">
              <SendIcon />
              Generate all
            </Button>
          </GenerateAllDialog>
        </SubHeader.Right>
      </SubHeader>
      <div className="flex flex-col flex-1 overflow-y-auto">
        <ReportsList
          isLoading={fetching}
          accessions={accessions}
          queuedIds={queuedIds}
          onGenerated={markSubmitted}
        />
      </div>
      <CommandMenuProvider>
        <CommandMenu>
          <ReportsCommands accessions={accessions || []} />
        </CommandMenu>
      </CommandMenuProvider>
    </>
  );
}

function ReportsCommands({ accessions }: { accessions: TAccession[] }) {
  const { setOpen, page } = useCommandMenu();

  const refresh = trpc.accession.refreshStatus.useMutation(
    mutationResponse({
      errorTitle: "Failed to refresh accession status",
      onSuccess(data) {
        if (!data?.length) {
          toast.success("All accessions already up to date");
        } else {
          toast.success(`${data.length} accessions updated`);
        }
      },
    }),
  );

  if (page || !accessions.length) return null;
  return (
    <CommandGroup heading="Reports">
      <CommandItem
        onSelect={() => {
          refresh.mutate({
            accessions: accessions.map((a) => ({ id: a.id, status: a.status })),
          });
          setOpen(false);
        }}
      >
        <RefreshCcwDotIcon />
        Refresh accession status
      </CommandItem>
    </CommandGroup>
  );
}
